import { useCallback, useEffect, useMemo, useState } from 'react';
import { filter, uniq } from 'lodash-es';
import { useDebounce } from '@namespace/helpers';
import {
  IS_USE_PLEX_API,
  MIN_GAMES_SEARCH_QUERY_LENGTH
} from '../../constants';
import { useGamesStore } from '../../store';
import { getGames } from '../../utils';
import { usePrepareCategoriesForSearchFilter } from '../usePrepareCategoriesForSearchFilter';
import { useSearchGamesPlex } from '../useSearchGamesPlex';

const useSearchGamesOld = ({ feedName, query = '', filterState = {} }) => {
  const { feeds, games: gamesFromStore } = useGamesStore(['feeds', 'games']);

  const gamesFeed = feeds[feedName];
  const lowerQuery = query.toLowerCase();
  const debouncedQuery = useDebounce(lowerQuery, 500);
  const isFeedLoaded = gamesFeed?.isLoaded;

  const [result, setResult] = useState({
    gamesResult: undefined,
    debouncedQuery: ''
  });

  const preparedCategories = usePrepareCategoriesForSearchFilter(feedName);

  const serviceGameIds = useMemo(() => {
    const { providersGames, gamesIds } = gamesFeed || {};

    const {
      categories: filterCategories = [],
      providers: filterProviders = []
    } = filterState || {};

    const getGamesIdsByProviders = () =>
      filterProviders.reduce((acc, p) => [...acc, ...providersGames[p]], []);
    const getGamesIdsByCategories = () =>
      filterCategories.reduce(
        (acc, c) => [...acc, ...preparedCategories.data[c].games],
        []
      );

    if (filterCategories.length && !filterProviders.length) {
      return getGamesIdsByCategories();
    }
    if (filterProviders.length && !filterCategories.length) {
      return getGamesIdsByProviders();
    }
    if (filterCategories.length && filterProviders.length) {
      const allCategoriesGamesIds = getGamesIdsByCategories();
      const allProvidersGamesIds = getGamesIdsByProviders();
      return allProvidersGamesIds.filter((gameId) =>
        allCategoriesGamesIds.includes(gameId)
      );
    }
    return gamesIds;
  }, [filterState, preparedCategories, gamesFeed]);

  const serviceGames = useMemo(
    () => getGames(uniq(serviceGameIds), gamesFromStore.data),
    [serviceGameIds, gamesFromStore]
  );

  const isFilterStateFilled =
    filterState.categories?.length || filterState.providers?.length;

  const filterGames = useCallback(
    (searchQuery = '', games) => {
      if (!isFeedLoaded) {
        return;
      }

      let searchResult;
      if (searchQuery && searchQuery.length >= MIN_GAMES_SEARCH_QUERY_LENGTH) {
        searchResult = filter(games, ({ name }) =>
          name.toLowerCase().includes(searchQuery)
        );
      } else if (isFilterStateFilled) {
        searchResult = games;
      }
      setResult({
        gamesResult: searchResult,
        debouncedQuery: searchQuery
      });
    },
    [isFeedLoaded, isFilterStateFilled]
  );

  useEffect(() => {
    filterGames(debouncedQuery, serviceGames);
  }, [filterGames, debouncedQuery, serviceGames]);

  return {
    ...result,
    totalGames: result.gamesResult?.length
  };
};

export const useSearchGames = IS_USE_PLEX_API
  ? useSearchGamesPlex
  : useSearchGamesOld;
